import { type Dispatch, type SetStateAction, useCallback, useEffect, useRef, useState } from 'react';
import { isNewerSnapshot } from './snapshotOrder';
import { useRushMidMatch, type RushMidSnapshot } from './useRushMidMatch';

const POLL_MS = 1000;

export interface SnapshotSync {
  snapshot: RushMidSnapshot | null;
  status: RushMidSnapshot['status'];
  apply: (next: RushMidSnapshot) => void;
  setSnapshot: Dispatch<SetStateAction<RushMidSnapshot | null>>;
  generation: () => number;
}

export function useSnapshotSync(mm: ReturnType<typeof useRushMidMatch>): SnapshotSync {
  const [snapshot, setSnapshot] = useState<RushMidSnapshot | null>(null);
  const status = snapshot?.status ?? 'NONE';

  const cmdGen = useRef(0);

  const apply = useCallback((next: RushMidSnapshot) => {
    cmdGen.current++;
    setSnapshot((prev) => (isNewerSnapshot(next, prev) ? next : prev));
  }, []);

  const foldBackground = useCallback((prev: RushMidSnapshot | null, s: RushMidSnapshot) => {
    if (prev?.status === 'OVER' && s.status === 'NONE') return prev;
    if (s.status === 'OVER' && !(prev && 'matchId' in prev)) return prev;
    return isNewerSnapshot(s, prev) ? s : prev;
  }, []);

  const setAuthoritative = useCallback<Dispatch<SetStateAction<RushMidSnapshot | null>>>((v) => {
    cmdGen.current++;
    setSnapshot(v);
  }, []);

  const resumedRef = useRef(false);
  useEffect(() => {
    if (resumedRef.current) return;
    resumedRef.current = true;
    mm.getSnapshot().then((s) => {
      if (s.status === 'OVER') return;
      setSnapshot((prev) => {
        if (prev && s.status === 'NONE') return prev;
        return isNewerSnapshot(s, prev) ? s : prev;
      });
    }).catch(() => {});
  }, [mm]);

  // WHY: TMA webview полностью останавливает JS, пока юзер в чате Telegram — интервальный опрос замирает, нужен fetch по
  // visibilitychange
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      const gen = cmdGen.current;
      mm.getSnapshot().then((s) => {
        if (cmdGen.current !== gen) return;
        setSnapshot((prev) => foldBackground(prev, s));
      }).catch(() => {});
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [mm, foldBackground]);

  useEffect(() => {
    if (status !== 'PARTY' && status !== 'OPEN' && status !== 'BUYING' && status !== 'PLAYING' && status !== 'OVER') return;
    const id = window.setInterval(() => {
      const gen = cmdGen.current;
      mm.getSnapshot().then((s) => {
        if (cmdGen.current !== gen) return;
        setSnapshot((prev) => foldBackground(prev, s));
      }).catch(() => {});
    }, POLL_MS);
    return () => window.clearInterval(id);
  }, [status, mm, foldBackground]);

  return { snapshot, status, apply, setSnapshot: setAuthoritative, generation: useCallback(() => cmdGen.current, []) };
}
